import React, {
  useEffect,
  useMemo,
  useState,
} from "react"
import {
  Box, Button, Grid, Heading, HStack, Text, VStack,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import { asMoney } from "utilities/strings"
import Panel from "components/elements/Panel"
import { Account, Plan } from "sharedTypes"
import { accountPlanPath, useQuery } from "utilities/routes"
import { Redirect } from "react-router-dom"
import { createCheckoutSession, createBillingPortalSession } from "api/accounts"
import BillingIntervalToggle from "pages/Account/Plan/BillingIntervalToggle"

type Props = {
  plans: Plan[]
  currentAccount: Account
  defaultSelectedPlan?: string
}

const PlansGrid = ({
  plans, currentAccount, defaultSelectedPlan,
}: Props) => {
  const { subscription } = currentAccount
  const query = useQuery()
  const type = query.get("plan")
  const currentPlan = plans.find((p) => p.type.toLowerCase() === subscription?.type.toLowerCase())
  const initialPlan = plans.find((p) => p.type.toLowerCase() === type?.toLowerCase())
    || plans.find((p) => p.type.toLowerCase() === defaultSelectedPlan?.toLowerCase())
    || currentPlan
    || plans[0]

  // `initialPlan` can be undefined when `plans` is empty.
  // Ensure `selectedPlan` is always defined so we don't crash while evaluating
  // `selectedPlan.type` in the `useEffect` dependency array.
  const [selectedPlan, setSelectedPlan] = useState<Plan>(initialPlan ?? ({} as Plan))
  const [selectedInterval, setSelectedInterval] = useState<"month" | "year">(subscription?.interval || "year")

  const orderedPlans = useMemo(
    () => {
      const byType = plans.reduce(
        (acc, plan) => {
          acc[plan.type] = plan

          return acc
        },
        {} as Record<string, Plan>,
      )
      const inPriorityOrder = PLAN_ORDER
        .map((planType) => byType[planType])
        .filter(Boolean) as Plan[]

      // Fallback to whatever order is provided if priority types are not available
      if (inPriorityOrder.length === 0) {
        return plans
      }

      // Append any other plan types returned by the backend after the known tiers.
      const remainingPlans = plans.filter(
        (plan) => !PLAN_ORDER.includes(plan.type),
      )

      return [...inPriorityOrder, ...remainingPlans]
    },
    [plans],
  )
  const currentPlanRank = currentPlan ? PLAN_ORDER.indexOf(currentPlan.type) : -1
  const isReactivatingSubscription = Boolean(subscription?.expired)
  const visiblePlans = useMemo(
    () => {
      // When reactivating a cancelled subscription, don't hide lower tiers.
      if (isReactivatingSubscription) {
        // But still hide the Free tier.
        return orderedPlans.filter((plan) => plan.type !== "Free")
      }

      if (currentPlanRank < 0) {
        return orderedPlans
      }

      return orderedPlans.filter(
        (plan) => PLAN_ORDER.indexOf(plan.type) >= currentPlanRank,
      )
    },
    [currentPlanRank, orderedPlans, isReactivatingSubscription],
  )

  // Ensure the highlighted/selected column always exists in `visiblePlans`.
  // If the initially selected plan is filtered out, clamp it to the first visible plan
  // whose tier is equal or higher than the initially selected plan.
  useEffect(() => {
    if (!visiblePlans.length) return

    const selectedIsVisible = visiblePlans.some((p) => p.type === selectedPlan.type)
    if (selectedIsVisible) return

    const selectedRank = PLAN_ORDER.indexOf(selectedPlan.type)
    const clampedPlan = selectedRank >= 0
      ? visiblePlans.find((p) => PLAN_ORDER.indexOf(p.type) >= selectedRank)
      : undefined

    const nextPlan = clampedPlan ?? visiblePlans[0]
    if (nextPlan && nextPlan.type !== selectedPlan.type) {
      setSelectedPlan(nextPlan)
    }
  }, [selectedPlan.type, visiblePlans])

  if (!initialPlan) {
    return <Redirect to={accountPlanPath()} />
  }

  return (
    <Box w={1320} maxW="100%" m="auto" p={6} bg="white">
      {subscription?.legacy && (
      <Panel bgColor="gray.100">
        <Text fontWeight="semibold">Your Current Plan (Legacy {subscription?.type}) is no longer available.</Text>
        See our new plans below.
      </Panel>
      )}
      <PlansComparisonTable
        plans={visiblePlans}
        subscriptionType={subscription?.type}
        currentSubscriptionPlan={currentPlan}
        currentAccount={currentAccount}
        selectedInterval={selectedInterval}
        setSelectedInterval={setSelectedInterval}
        selectedPlan={selectedPlan}
        onSelectPlan={setSelectedPlan}
      />
    </Box>
  )
}

type Interval = "month" | "year"

type ComparisonPlan = Plan & { type: string }

type ComparisonTableProps = {
  plans: ComparisonPlan[]
  subscriptionType?: string | null
  currentSubscriptionPlan?: Plan | null
  currentAccount: Account
  selectedInterval: Interval
  setSelectedInterval: (interval: Interval) => void
  selectedPlan: Plan
  onSelectPlan: (plan: Plan) => void
}

type FeatureRowConfig = {
  label: string
  values?: Record<string, string | boolean>
  getValue?: (plan: Plan) => string | boolean
}

type FeatureSectionConfig = {
  title: string
  icon?: string
  iconBg?: string
  rows: FeatureRowConfig[]
}

const PLAN_ORDER: string[] = ["Free", "Professional", "Corporate", "Enterprise"]

const ROW_HOVER_BG = "gray.50"

// Included in `tier` and every plan above it, as on d.io/pricing.
const includedFrom = (tier: string) => Object.fromEntries(
  PLAN_ORDER.map((planType, rank) => [planType, rank >= PLAN_ORDER.indexOf(tier)]),
)

const FEATURE_SECTIONS: FeatureSectionConfig[] = [
  {
    title: "Account",
    icon: "lock",
    iconBg: "#555555",
    rows: [
      {
        label: "Licenses",
        getValue: (plan) => {
          if (plan.teamLimit >= 15) {
            return "15+"
          }

          return plan.teamLimit.toLocaleString()
        },
      },
      {
        label: "Events",
        // Keep this row in sync with backend-enforced limits.
        getValue: (plan) => {
          if (plan.eventLimit == null) {
            return "Unlimited"
          }

          return plan.eventLimit.toLocaleString()
        },
      },
      { label: "User-Based Permissions", values: includedFrom("Professional") },
      { label: "Mobile Apps for iOS & Android", values: includedFrom("Free") },
    ],
  },
  {
    title: "Guest Management",
    icon: "guests",
    iconBg: "#2996CC",
    rows: [
      {
        label: "Guests per Event",
        // Keep this in sync with the backend `guestLimit` values.
        getValue: (plan) => {
          if (plan.guestLimit == null) {
            return "Unlimited"
          }

          return plan.guestLimit.toLocaleString()
        },
      },
      { label: "Complete Guest Profiles", values: includedFrom("Free") },
      { label: "Plus-One Management", values: includedFrom("Professional") },
      { label: "Guest Profile Enrichment", getValue: (plan) => plan.enrichGuests },
      { label: "Custom Filters & Segments", getValue: (plan) => plan.segments },
    ],
  },
  {
    title: "Check-In & QR Codes",
    icon: "portal-confirmed",
    iconBg: "#5471CB",
    rows: [
      { label: "Basic Check-In", values: includedFrom("Free") },
      { label: "Session Tracking", getValue: (plan) => plan.checkInLocations },
      { label: "Check-In Alerts", getValue: (plan) => plan.checkInAlerts },
      { label: "Multi-Mode QR Scanner", getValue: (plan) => plan.qrCodes },
      { label: "Access Rules & Duplicate Scan Controls", getValue: (plan) => plan.qrCodes },
      { label: "Fraud-Proof Rotating QR Codes", getValue: (plan) => plan.expiringQrCodes },
    ],
  },
  {
    title: "Forms",
    icon: "forms",
    iconBg: "#F6C914",
    rows: [
      { label: "Registration Forms", values: includedFrom("Free") },
      { label: "Ticket Forms", getValue: (plan) => plan.ticketForms },
      { label: "RSVP Forms", getValue: (plan) => plan.rsvpForms },
      { label: "Waitlist Forms", getValue: (plan) => plan.waitlistForms },
      { label: "Survey Forms", values: includedFrom("Corporate") },
    ],
  },
  {
    title: "Guest Portal",
    icon: "guest-portal",
    iconBg: "#F6C914",
    rows: [
      { label: "Automated Confirmations", values: includedFrom("Free") },
      { label: "Calendar Invites", values: includedFrom("Free") },
      { label: "Video Banners", values: includedFrom("Professional") },
      { label: "Branded Guest Experience", values: includedFrom("Corporate") },
      { label: "Multi-Language Events", getValue: (plan) => plan.customLanguageTemplates },
      { label: "Custom Apple & Google Wallet Passes", getValue: (plan) => plan.passBuilder },
      { label: "Zapier Integrations", getValue: (plan) => plan.zapier },
    ],
  },
  {
    title: "Emails & Invitations",
    icon: "emails",
    iconBg: "#1CBDA2",
    rows: [
      {
        label: "Bulk Emails per Day",
        values: {
          Free: false,
          Professional: "500",
          Corporate: "5,000",
          Enterprise: "15,000",
        },
      },
      { label: "QR Code Distribution", getValue: (plan) => plan.qrCodes },
      { label: "CC Guest Representatives", values: includedFrom("Corporate") },
      { label: "White-Label Emails", getValue: (plan) => plan.whiteLabelEmails },
      { label: "Custom Email Domains", getValue: (plan) => plan.whiteLabelEmails },
    ],
  },
  {
    title: "Event Webpage",
    icon: "website",
    iconBg: "#49009F",
    rows: [
      { label: "Vanity URLs on d.io", getValue: (plan) => plan.vanityUrls },
      { label: "Custom Event Webpage", values: includedFrom("Professional") },
      { label: "Multi-Day & Session Agendas", getValue: (plan) => plan.eventAgenda },
      { label: "Speaker Profiles", getValue: (plan) => plan.speakerProfiles },
      { label: "White-Label Webpage", getValue: (plan) => plan.whiteLabelEmails },
    ],
  },
  {
    title: "Ticketing",
    icon: "ticketing",
    iconBg: "#26B5ED",
    rows: [
      { label: "Secure Payments via Stripe", values: includedFrom("Free") },
      { label: "Apple Pay & Google Pay Checkout", values: includedFrom("Free") },
      { label: "Point of Sale Integration", values: includedFrom("Free") },
      { label: "Shareable Short Ticket Links", values: includedFrom("Free") },
      { label: "Embeddable Ticket Checkout for Your Website", values: includedFrom("Free") },
    ],
  },
  {
    title: "Seating Charts",
    icon: "seating",
    iconBg: "#CF7C00",
    rows: [
      {
        label: "Drag & Drop Seating",
        getValue: (plan) => (plan.tableLimit == null ? true : `${plan.tableLimit.toLocaleString()} tables`),
      },
      { label: "Placeholders & Waitlists", values: includedFrom("Professional") },
      { label: "Real-Time Seat Availability", getValue: (plan) => plan.realTimeVacancy },
      { label: "Shareable Table Links", getValue: (plan) => plan.shareTablesExternally },
    ],
  },
  {
    title: "Badge Printing",
    icon: "badges",
    iconBg: "#CD538A",
    rows: [
      { label: "Drag & Drop Badge Builder", getValue: (plan) => plan.badgePrinting },
      { label: "On-Site Printing from Mobile", getValue: (plan) => plan.badgePrinting },
      { label: "Desktop & PDF Badge Printing", getValue: (plan) => plan.badgePrinting },
      { label: "Avery Sheet Template Support", getValue: (plan) => plan.badgePrinting },
    ],
  },
  {
    title: "Text Messaging",
    icon: "texts",
    iconBg: "#FF453A",
    rows: [
      { label: "SMS & WhatsApp Messaging", getValue: (plan) => plan.texts },
      { label: "Meta-Approved WhatsApp Templates", getValue: (plan) => plan.texts },
      { label: "Automatic WhatsApp Fallback to SMS", getValue: (plan) => plan.texts },
      { label: "Real-Time Delivery Tracking", getValue: (plan) => plan.texts },
      { label: "Send Messages from the Mobile App", getValue: (plan) => plan.texts },
    ],
  },
  {
    title: "Kiosk Mode",
    icon: "kiosk",
    iconBg: "purple.700",
    rows: [
      { label: "Branded Self-Service Kiosk on Any iPad", getValue: (plan) => plan.kioskMode },
      { label: "QR Scanning for Check-In & Sessions", getValue: (plan) => plan.kioskMode },
      { label: "Walk-In Registration via Diobox Forms", getValue: (plan) => plan.kioskMode },
      { label: "Automatic Badge Printing", getValue: (plan) => plan.kioskMode },
    ],
  },
  {
    title: "Lead Retrieval",
    icon: "tablet",
    iconBg: "#00A700",
    rows: [
      { label: "Dedicated Mobile Apps", getValue: (plan) => plan.leadRetrieval },
      { label: "QR Contact Info Capture", getValue: (plan) => plan.leadRetrieval },
      { label: "Lead Classification & Notes", getValue: (plan) => plan.leadRetrieval },
      { label: "Exhibitor License Management", getValue: (plan) => plan.leadRetrieval },
    ],
  },
]

const PlansComparisonTable = ({
  plans,
  subscriptionType,
  currentSubscriptionPlan,
  currentAccount,
  selectedInterval,
  setSelectedInterval,
  selectedPlan,
  onSelectPlan,
}: ComparisonTableProps) => {
  const [redirectingPlanType, setRedirectingPlanType] = useState<string | null>(null)
  const [hoveredFeatureRowIndex, setHoveredFeatureRowIndex] = useState<number | null>(null)

  const planTypesInTable = useMemo(
    () => PLAN_ORDER.filter((type) => plans.some((plan) => plan.type === type)),
    [plans],
  )

  if (planTypesInTable.length === 0) {
    return null
  }

  const tablePlans = planTypesInTable
    .map((planType) => plans.find((plan) => plan.type === planType)!)
    .filter(Boolean)
  const isExpiredSubscription = Boolean(currentAccount.subscription?.expired)

  const headerPrice = (plan: Plan) => {
    const price = selectedInterval === "year"
      ? plan.annualPrice
      : plan.monthlyPrice

    return asMoney(price, "USD", true)
  }

  const getActionLabel = (plan: Plan) => {
    if (isExpiredSubscription) {
      return "Upgrade"
    }

    const isCurrent = currentSubscriptionPlan
      ? currentSubscriptionPlan.type.toLowerCase() === plan.type.toLowerCase()
      : subscriptionType
        ? subscriptionType.toLowerCase() === plan.type.toLowerCase()
        : plan.type === "Free"

    if (isCurrent) {
      return "Current Plan"
    }

    if (!currentSubscriptionPlan) {
      return "Upgrade"
    }

    const currentPrice = selectedInterval === "year"
      ? currentSubscriptionPlan.annualPrice / 12
      : currentSubscriptionPlan.monthlyPrice

    const price = selectedInterval === "year"
      ? plan.annualPrice / 12
      : plan.monthlyPrice

    if (price < currentPrice) {
      return "Downgrade"
    }

    if (price > currentPrice) {
      return "Upgrade"
    }

    return ""
  }

  const showProrationBanner = !isExpiredSubscription
    && Boolean(currentAccount.subscription?.stripeId)
    && tablePlans.some((plan) => getActionLabel(plan) === "Upgrade")

  const renderCell = (value: string | boolean | undefined) => {
    if (value === "" || value === undefined || value === null) {
      return null
    }

    if (typeof value === "boolean") {
      if (!value) {
        return null
      }

      return <Icon icon="checkmark" size={5} color="blue.500" />
    }

    return <Text fontSize="md" fontWeight="semibold">{value}</Text>
  }

  const handlePlanStripeRedirect = async (plan: Plan) => {
    setRedirectingPlanType(plan.type)
    try {
      const { subscription } = currentAccount
      const stripePriceId = plan.stripePriceIds[selectedInterval]

      const { data } = subscription
        ? await createBillingPortalSession({
          flow: "subscription_update_confirm",
          stripePriceId,
        })
        : await createCheckoutSession({
          stripePriceId,
        })

      sessionStorage.setItem("checkoutReturnPath", window.location.pathname)
      window.location.assign(data.url)
    } catch (error) {
      setRedirectingPlanType(null)
    }
  }

  const totalFeatureRows = FEATURE_SECTIONS.reduce(
    (count, section) => count + section.rows.length,
    0,
  )
  const getColumnShadow = (
    planType: string,
    edges: ("top" | "bottom" | "left" | "right")[],
  ) => {
    const isSelected = selectedPlan.type === planType
    if (!isSelected) return "none"

    const c = "var(--chakra-colors-blue-400)"
    const parts: string[] = []
    if (edges.includes("left")) parts.push(`inset 1px 0 0 ${c}`)
    if (edges.includes("right")) parts.push(`inset -1px 0 0 ${c}`)
    if (edges.includes("top")) parts.push(`inset 0 1px 0 ${c}`)
    if (edges.includes("bottom")) parts.push(`inset 0 -1px 0 ${c}`)

    return parts.join(", ")
  }

  const getColumnStyles = (planType: string) => {
    const isSelected = selectedPlan.type === planType

    return {
      isSelected,
      isActive: isSelected,
      bg: isSelected ? "blue.50" : "white",
    }
  }

  let runningRowIndex = -1

  return (
    <Box borderRadius="md">
      <Box pt={6} bg="white">
        <VStack spacing={5} align="center" mb={12} px={4}>
          <Heading mb={3} fontSize="3xl" fontWeight="semibold" textAlign="center" w="100%">
            Choose your plan
          </Heading>
          <BillingIntervalToggle
            value={selectedInterval}
            onChange={setSelectedInterval}
            layoutDependency={plans}
          />
          {showProrationBanner && (
            <HStack
              align="center"
              spacing={3}
              py={2}
              px={5}
              w="fit-content"
              justify="center"
              bg="blue.50"
              borderRadius="full"
            >
              <Icon icon="orders-outline" size={5} color="blue.500" flexShrink={0} />
              <Text fontSize="sm" color="blue.600" textAlign="center" lineHeight="short">
                {"You're billed prorated — click "}
                <Text as="span" fontWeight="bold" color="blue.700">Upgrade</Text>
                {" to preview your exact price"}
              </Text>
            </HStack>
          )}
        </VStack>
      </Box>

      <Grid
        templateColumns={`minmax(200px, 1.5fr) repeat(${tablePlans.length}, minmax(180px, 1fr))`}
        bg="white"
        borderBottomWidth={1}
        position="sticky"
        top={0}
        zIndex={10}
        pt={4}
      >
        {/* Extend sticky header background beyond table edges */}
        <Box
          position="absolute"
          top={0}
          bottom={0}
          left={-2}
          right={-2}
          bg="white"
          pointerEvents="none"
          zIndex={-1}
          borderRadius="md"
        />
        {/* Plan headers */}
        <Box px={8} py={4} />
        {tablePlans.map((plan) => {
          const isCurrent = currentSubscriptionPlan
            ? currentSubscriptionPlan.type.toLowerCase() === plan.type.toLowerCase()
            : subscriptionType
              ? subscriptionType.toLowerCase() === plan.type.toLowerCase()
              : plan.type === "Free"
          const columnStyles = getColumnStyles(plan.type)
          const actionLabel = getActionLabel(plan)

          return (
            <Box
              key={plan.type}
              px={8}
              py={4}
              bg={columnStyles.bg}
              boxShadow={getColumnShadow(plan.type, ["left", "right", "top"])}
              borderTopLeftRadius={columnStyles.isActive ? "md" : 0}
              borderTopRightRadius={columnStyles.isActive ? "md" : 0}
              textAlign="center"
              display="flex"
              flexDirection="column"
            >
              <Box>
                <Text fontWeight="extrabold" fontSize="2xl" mb={1}>
                  {plan.type}
                </Text>
                <Text fontSize="2xl" fontWeight="semibold" mb={0}>
                  <Text as="span" fontSize="sm" verticalAlign="top" mr={1}>
                    {headerPrice(plan).charAt(0)}
                  </Text>
                  {headerPrice(plan).slice(1)}
                </Text>
                {plan.type !== "Free" && (
                  <Text fontSize="md" mb={1}>
                    Billed
                    {" "}
                    {selectedInterval === "year" ? "Yearly" : "Monthly"}
                  </Text>
                )}
              </Box>
              <Box mt="auto" pt={4}>
                {isCurrent && !isExpiredSubscription && (
                  <Button
                    size="md"
                    px={6}
                    w="100%"
                    fontSize="lg"
                    fontWeight="bold"
                    variant="outline"
                    colorScheme="blue"
                    isDisabled
                  >
                    Current Plan
                  </Button>
                )}
                {isCurrent && isExpiredSubscription && (
                  <Button
                    size="md"
                    px={6}
                    w="100%"
                    fontSize="lg"
                    fontWeight="bold"
                    variant="solid"
                    colorScheme="blue"
                    isLoading={redirectingPlanType === plan.type}
                    onClick={() => {
                      onSelectPlan(plan)
                      handlePlanStripeRedirect(plan)
                    }}
                  >
                    Upgrade
                  </Button>
                )}
                {!isCurrent && actionLabel && (
                  <Button
                    size="md"
                    px={6}
                    w="100%"
                    fontSize="lg"
                    fontWeight="bold"
                    variant="solid"
                    colorScheme={actionLabel === "Downgrade" ? "gray" : "blue"}
                    isLoading={redirectingPlanType === plan.type}
                    onClick={() => {
                      onSelectPlan(plan)
                      handlePlanStripeRedirect(plan)
                    }}
                  >
                    {actionLabel}
                  </Button>
                )}
              </Box>
            </Box>
          )
        })}
      </Grid>

      {/* Feature sections */}
      {FEATURE_SECTIONS.map((section, sectionIndex) => (
        <Box key={section.title}>
          <Grid
            templateColumns={`minmax(200px, 1.5fr) repeat(${tablePlans.length}, minmax(180px, 1fr))`}
            borderBottomWidth={1}
            bg="white"
          >
            <Box px={8} py={3} pt={8} bg="white">
              <HStack spacing={3}>
                {section.icon && (
                  <Box
                    w={10}
                    h={10}
                    borderRadius="md"
                    bg={section.iconBg || "blue.500"}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon icon={section.icon} color="white" size={6} />
                  </Box>
                )}
                <Text fontWeight="bold" fontSize="xl">
                  {section.title}
                </Text>
              </HStack>
            </Box>
            {tablePlans.map((plan) => {
              const columnStyles = getColumnStyles(plan.type)

              return (
                <Box
                  key={`${section.title}-${plan.type}-header`}
                  px={4}
                  py={3}
                  bg={columnStyles.bg}
                  boxShadow={getColumnShadow(plan.type, ["left", "right"])}
                />
              )
            })}
          </Grid>
          {section.rows.map((row) => {
            runningRowIndex += 1
            const isFirstFeatureRow = runningRowIndex === 0
            const isLastFeatureRow = runningRowIndex === totalFeatureRows - 1
            const rowIndex = runningRowIndex
            const isRowHovered = hoveredFeatureRowIndex === rowIndex

            return (
              <Box
                key={`${sectionIndex}-${row.label}`}
                onMouseEnter={() => setHoveredFeatureRowIndex(rowIndex)}
                onMouseLeave={() => setHoveredFeatureRowIndex(null)}
              >
                <Grid
                  templateColumns={`minmax(200px, 1.5fr) repeat(${tablePlans.length}, minmax(180px, 1fr))`}
                  borderBottomWidth={isLastFeatureRow ? 0 : 1}
                >
                  <Box px={8} py={3} bg={isRowHovered ? ROW_HOVER_BG : "white"}>
                    <Text fontSize="md" fontWeight="semibold">{row.label}</Text>
                  </Box>
                  {tablePlans.map((plan) => {
                    const cellValue = row.getValue
                      ? row.getValue(plan)
                      : row.values?.[plan.type]

                    const isSelectedPlanColumn = plan.type === selectedPlan.type
                    const columnStyles = getColumnStyles(plan.type)

                    return (
                      <Box
                        key={plan.type}
                        px={8}
                        py={3}
                        bg={
                          isRowHovered
                            ? isSelectedPlanColumn
                              ? columnStyles.bg
                              : ROW_HOVER_BG
                            : isSelectedPlanColumn
                              ? columnStyles.bg
                              : "white"
                        }
                        textAlign="center"
                        boxShadow={getColumnShadow(
                          plan.type,
                          isLastFeatureRow ? ["left", "right", "bottom"] : ["left", "right"],
                        )}
                        borderTopWidth={isFirstFeatureRow ? 0 : undefined}
                        borderBottomWidth={isLastFeatureRow ? 0 : undefined}
                        borderBottomLeftRadius={isSelectedPlanColumn && isLastFeatureRow ? "md" : 0}
                        borderBottomRightRadius={isSelectedPlanColumn && isLastFeatureRow ? "md" : 0}
                      >
                        {renderCell(cellValue)}
                      </Box>
                    )
                  })}
                </Grid>
              </Box>
            )
          })}
        </Box>
      ))}
    </Box>
  )
}

export default PlansGrid
